import React, { useState, useMemo, useEffect } from 'react';
import { getUserInitials, getAvatarBgColor } from '../../utils/avatarUtils';
import './UserAvatar.css';

export interface UserAvatarProps {
  name: string;
  photoURL?: string | null;
  /** Used for background color when no photo; falls back to `name`. */
  userId?: string;
  className?: string;
  style?: React.CSSProperties;
  title?: string;
}

/**
 * Renders a profile photo when `photoURL` is set, otherwise initials on a colored circle.
 * Works with `.user-avatar` (header), `.pm-avatar`, `.tc-avatar`, etc.
 */
const UserAvatar: React.FC<UserAvatarProps> = ({ name, photoURL, userId, className = 'pm-avatar', style, title }) => {
  const [imgError, setImgError] = useState(false);
  const initials = useMemo(() => getUserInitials(name), [name]);
  const bg = useMemo(() => getAvatarBgColor(userId || name || '?'), [userId, name]);

  const urlTrimmed = typeof photoURL === 'string' ? photoURL.trim() : '';
  useEffect(() => {
    setImgError(false);
  }, [urlTrimmed]);

  const show = Boolean(urlTrimmed && !imgError);

  return (
    <span
      className={className}
      style={show ? { ...style, background: 'transparent' } : { ...style, background: bg }}
      title={title ?? name}
    >
      {show ? (
        <img
          src={urlTrimmed}
          alt=""
          className="user-avatar-img"
          onError={() => setImgError(true)}
        />
      ) : (
        initials
      )}
    </span>
  );
};

export default UserAvatar;
